/**
 * RuleCodePanel — shows the code a DQ rule actually runs, and lets a steward
 * edit it.
 *
 * A rule is one of two things: raw SQL (`condition_sql`, a SELECT returning one
 * row per violation, with `{schema}` for the source schema) or the structured
 * JSON DSL. The SQL tab and the JSON tab show the same rule two ways; whichever
 * one the rule is defined in is the editable one.
 *
 * Project rules are editable here (PUT /dq-rules/{key}; the server re-validates
 * the SQL with the rule SQL guard and sends an approved rule back to draft when
 * its code changes). Knowledge-pack rules are read-only: "Reuse in project to
 * edit" copies one into the project first.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Code2, Copy, Loader2, Play, Save, X } from "lucide-react";

import type { CompiledRuleSql } from "../../api/rules";
import type { ActionGate } from "../../lib/rulesPermissions";
import { CompiledSqlPreview } from "./CompiledSqlPreview";
import { useFocusTrap } from "../../shared/hooks/use-focus-trap";
import { GatedButton } from "./RulesPermissionNotice";

export interface RuleCode {
  key: string;
  label: string;
  /** Lifecycle status for project rules; omitted for pack rules. */
  status?: string;
  /** Where the rule comes from, shown under the title. */
  origin: "project" | "knowledge_base";
  expr_kind: "structured" | "condition_sql";
  condition_sql: string | null;
  population_sql: string | null;
  structured_expression: Record<string, unknown>;
  table_name: string | null;
  severity: string;
  category: string;
  derived_from?: string | null;
}

export type RuleCodeChange =
  | { expr_kind: "condition_sql"; condition_sql: string }
  | { expr_kind: "structured"; structured_expression: Record<string, unknown> };

// Mirrors DatabaseRuleSource.update: a code change on a rule in any other
// status sends it back to draft.
const UNREVIEWED_STATUSES = new Set(["draft", "retired"]);

export function RuleCodePanel({
  rule,
  editable,
  editGate,
  runGate,
  onSave,
  onClose,
  onReuseToEdit,
  reuseGate,
  compileSql,
}: {
  rule: RuleCode;
  /** True for project rules; pack rules are never edited in place. */
  editable: boolean;
  editGate: ActionGate;
  runGate: ActionGate;
  /** Save the change (null: nothing changed); when `run` is set, run the
   * saved rule straight after. */
  onSave: (change: RuleCodeChange | null, run: boolean) => Promise<void>;
  onClose: () => void;
  /** Pack rules: copy into the project as an editable draft. */
  onReuseToEdit?: () => Promise<void>;
  reuseGate?: ActionGate;
  /** Structured rules: the SQL the JSON compiles to, shown on the SQL tab. */
  compileSql?: (
    structuredExpression: Record<string, unknown>,
  ) => Promise<CompiledRuleSql>;
}) {
  const isSql = rule.expr_kind === "condition_sql";
  const [tab, setTab] = useState<"sql" | "json">(isSql ? "sql" : "json");
  const original = useMemo(
    () =>
      isSql
        ? (rule.condition_sql ?? "")
        : JSON.stringify(rule.structured_expression ?? {}, null, 2),
    [isSql, rule.condition_sql, rule.structured_expression],
  );
  const [draft, setDraft] = useState(original);
  const [busy, setBusy] = useState<"save" | "run" | "reuse" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setDraft(original);
    setErr(null);
  }, [original]);

  const dirty = draft !== original;
  const canEdit = editable && editGate.allowed;
  const resetsApproval =
    dirty && rule.status !== undefined && !UNREVIEWED_STATUSES.has(rule.status);

  // Never close mid-save (the save would reopen the panel), and never drop
  // unsaved edits without asking.
  const requestClose = () => {
    if (busy !== null) return;
    if (dirty && !window.confirm("Discard your unsaved changes to this rule?"))
      return;
    onClose();
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const containerRef = useFocusTrap(true);

  // The whole rule as JSON: what the rule IS, whichever form it's written in.
  const definitionJson = useMemo(
    () =>
      JSON.stringify(
        {
          rule_key: rule.key,
          label: rule.label,
          category: rule.category,
          severity: rule.severity,
          table: rule.table_name,
          expr_kind: rule.expr_kind,
          ...(isSql
            ? {
                condition_sql: draft,
                ...(rule.population_sql
                  ? { population_sql: rule.population_sql }
                  : {}),
              }
            : { structured_expression: rule.structured_expression }),
          ...(rule.derived_from ? { derived_from: rule.derived_from } : {}),
        },
        null,
        2,
      ),
    [rule, isSql, draft],
  );

  const save = async (run: boolean) => {
    let change: RuleCodeChange | null = null;
    if (!dirty) {
      // Nothing to save: just run the rule as stored.
    } else if (isSql) {
      if (!draft.trim()) {
        setErr("The rule's SQL can't be empty.");
        return;
      }
      change = { expr_kind: "condition_sql", condition_sql: draft };
    } else {
      try {
        const parsed: unknown = JSON.parse(draft);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          setErr("The rule's JSON must be an object.");
          return;
        }
        change = {
          expr_kind: "structured",
          structured_expression: parsed as Record<string, unknown>,
        };
      } catch (e) {
        setErr(`Not valid JSON: ${e instanceof Error ? e.message : String(e)}`);
        return;
      }
    }
    try {
      setBusy(run ? "run" : "save");
      setErr(null);
      await onSave(change, run);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const editorTab = isSql ? "sql" : "json";
  const showingEditor = tab === editorTab;

  const copy = async () => {
    try {
      // What's on screen: the editor's text, or the read-only definition.
      await navigator.clipboard.writeText(
        showingEditor ? draft : definitionJson,
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context) — nothing to do */
    }
  };

  const reuse = async () => {
    if (!onReuseToEdit) return;
    try {
      setBusy("reuse");
      setErr(null);
      await onReuseToEdit();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const textareaCls =
    "w-full min-h-[260px] font-mono text-xs leading-relaxed px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white/90 outline-none focus:ring-1 focus:ring-[var(--primary)] whitespace-pre";

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Rule code: ${rule.label}`}
    >
      <div
        ref={containerRef}
        className="glass-surface-heavy rounded-2xl p-5 max-w-4xl w-full max-h-[90vh] flex flex-col gap-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-white/90 flex items-center gap-2">
              <Code2 className="w-4 h-4 text-white/60" />
              {rule.label}
            </h3>
            <p className="text-xs text-white/50 mt-1">
              {rule.origin === "knowledge_base"
                ? "Knowledge-pack rule (read-only)"
                : `Project rule · ${rule.status ?? "draft"}`}
              {rule.table_name ? ` · ${rule.table_name}` : " · cross-table"}
              {rule.severity ? ` · ${rule.severity}` : ""}
            </p>
          </div>
          <button
            onClick={requestClose}
            className="text-white/50 hover:text-white/90"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div
          className="flex items-center gap-1 border-b border-white/10"
          role="tablist"
        >
          {(["sql", "json"] as const).map((t) => (
            <button
              key={t}
              role="tab"
              id={`rule-code-tab-${t}`}
              aria-controls="rule-code-panel"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs border-b-2 transition-colors ${
                tab === t
                  ? "border-blue-400 text-white"
                  : "border-transparent text-white/50 hover:text-white/70"
              }`}
            >
              {t.toUpperCase()}
            </button>
          ))}
          <button
            onClick={copy}
            className="ml-auto inline-flex items-center gap-1 text-xs text-white/50 hover:text-white/80 px-2 py-1"
          >
            <Copy className="w-3 h-3" />
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <div
          id="rule-code-panel"
          role="tabpanel"
          aria-labelledby={`rule-code-tab-${tab}`}
          className="overflow-y-auto custom-scrollbar space-y-3"
        >
          {showingEditor ? (
            <>
              <textarea
                aria-label={isSql ? "Rule SQL" : "Rule JSON"}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                readOnly={!canEdit}
                spellCheck={false}
                className={textareaCls}
              />
              <p className="text-[11px] text-white/45">
                {isSql
                  ? "One read-only SELECT that returns one row per violation. {schema} is replaced with the source schema when it runs."
                  : compileSql
                    ? "The structured rule definition. The server compiles it to SQL when it runs; the SQL tab shows that SQL."
                    : "The structured rule definition. The server compiles it to SQL when it runs."}
              </p>
              {isSql && rule.population_sql && (
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-wider text-white/50">
                    Population SQL (rows the rule applies to)
                  </div>
                  <pre className={`${textareaCls} min-h-0`}>
                    {rule.population_sql}
                  </pre>
                </div>
              )}
            </>
          ) : tab === "json" ? (
            <pre
              className={`${textareaCls} min-h-0 overflow-x-auto`}
              aria-label="Rule definition JSON"
            >
              {definitionJson}
            </pre>
          ) : compileSql ? (
            <CompiledSqlPreview
              autoLoad
              resetKey={original}
              preClassName={`${textareaCls} min-h-0 overflow-x-auto whitespace-pre-wrap`}
              load={() => compileSql(rule.structured_expression ?? {})}
            />
          ) : (
            <p className="text-xs text-white/60 rounded-md border border-white/10 bg-white/5 p-3">
              This rule is written in the structured JSON form (JSON tab). The
              server compiles it to SQL against the connected source when it
              runs.
            </p>
          )}
        </div>

        {resetsApproval && (
          <p className="text-xs text-amber-200 bg-amber-400/10 border border-amber-400/30 rounded-md px-3 py-2">
            Saving changes this rule's code, so it goes back to draft. It runs
            in profiling again once it's submitted and approved
            {rule.derived_from
              ? "; until then, the knowledge-pack rule it was copied from runs in its place."
              : "."}
          </p>
        )}
        {err && (
          <p
            role="alert"
            className="text-xs text-red-300 bg-red-400/10 border border-red-400/30 rounded-md px-3 py-2 whitespace-pre-wrap"
          >
            {err}
          </p>
        )}

        <div className="flex items-center justify-end gap-2">
          {!editable && onReuseToEdit && reuseGate && (
            <>
              <span className="mr-auto text-xs text-white/50">
                Pack rules are read-only. Reuse one in this project to edit a
                copy.
              </span>
              <GatedButton
                gate={reuseGate}
                onClick={reuse}
                disabled={busy !== null}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-[var(--primary)]/80 hover:bg-[var(--primary)] text-white font-medium disabled:opacity-50"
              >
                {busy === "reuse" && (
                  <Loader2 className="w-3 h-3 animate-spin" />
                )}
                Reuse in project to edit
              </GatedButton>
            </>
          )}
          {editable && (
            <>
              {dirty && (
                <button
                  onClick={() => {
                    setDraft(original);
                    setErr(null);
                  }}
                  disabled={busy !== null}
                  className="px-3 py-1.5 text-xs rounded-md text-white/70 hover:text-white disabled:opacity-50"
                >
                  Discard changes
                </button>
              )}
              <GatedButton
                gate={editGate}
                onClick={() => save(false)}
                disabled={!dirty || busy !== null}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-white/15 bg-white/5 hover:bg-white/10 text-white/85 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === "save" ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                Save
              </GatedButton>
              <GatedButton
                gate={dirty ? (editGate.allowed ? runGate : editGate) : runGate}
                onClick={() => save(true)}
                disabled={busy !== null}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-emerald-500/80 hover:bg-emerald-500 text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === "run" ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Play className="w-3 h-3" />
                )}
                {dirty ? "Save & run" : "Run"}
              </GatedButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
